import { Autocomplete, TextField } from '@elipssolution/harfang';
import { Stack, styled, Typography } from '@mui/material';
import getConfig from 'next/config';
import { Control, Controller, useWatch } from 'react-hook-form';

import SupportItem from './SupportItem';
import useFormat from '../../../hooks/useFormat';
import { ModuleType } from '../../../types/module';
import useModulesDataSource from '../../hooks/useModulesDataSource';
import { PublicRuntimeConfigType } from '../../types/publicRuntimeConfig';
import { RequestTypeEnum, SupportFormType } from '../../types/support';

type SupportModuleItemProps = {
	control: Control<SupportFormType>;
	onModuleChange: () => void;
};

const SuggestionTextField = styled(TextField)({
	'& > div > textarea': {
		minHeight: 150,
	},
});

const SupportModuleItem = ({ control, onModuleChange }: SupportModuleItemProps) => {
	const {
		publicRuntimeConfig: { appName },
	} = getConfig() as PublicRuntimeConfigType;

	const { isMobileFormat } = useFormat();
	const type = useWatch({ control, name: 'type' });

	const { dataSource: modulesDataSource } = useModulesDataSource();

	return (
		<SupportItem
			content={
				<Stack gap={2}>
					{type === RequestTypeEnum.ENHANCEMENT && (
						<>
							<Typography>
								Super ! {appName} est en constante évolution, n&apos;hésitez pas à nous faire parvenir vos idées.
							</Typography>
							<Typography>
								Dans un premier temps, quel module de {appName} est concerné par cette proposition d&apos;évolution ?
							</Typography>
						</>
					)}
					{type === RequestTypeEnum.BUG && (
						<>
							<Typography>Aïe ! Nous allons essayer de le corriger au plus tôt !</Typography>
							<Typography>Dans un premier temps, quel module de {appName} est concerné par ce bug ?</Typography>
						</>
					)}

					{type === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS && (
						<>
							<Typography>
								Merci d&apos;utiliser notre module de signature électronique. Veuillez indiquer le nombre d’invitations
								à signer souhaité, et toute précision utile.
							</Typography>
							<Typography>Notre équipe reviendra vers vous dans les plus brefs délais.</Typography>
						</>
					)}
				</Stack>
			}
			action={
				type === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS ? (
					<Stack marginTop={2}>
						<Controller
							control={control}
							name="message"
							render={({ field: { value, ...field } }) => (
								<SuggestionTextField
									{...field}
									placeholder="Description de votre demande d’achat..."
									value={value ?? ''}
									multiline
								/>
							)}
							rules={{ required: true }}
						/>
					</Stack>
				) : (
					<Stack flexDirection="row" justifyContent="flex-end" gap={1} marginTop={2}>
						<Controller
							control={control}
							render={({ field: { onChange, ...field } }) => (
								<Autocomplete<Pick<ModuleType, 'code' | 'name'>>
									{...field}
									dataKey="code"
									onChange={(module) => {
										onChange(module);
										onModuleChange();
									}}
									dataSource={modulesDataSource}
									getOptionLabel={({ name }) => name}
									label="Module concerné"
									sx={{
										width: isMobileFormat ? '100%' : 300,
									}}
								/>
							)}
							rules={{ required: true }}
							name="module"
						/>
					</Stack>
				)
			}
		/>
	);
};

export default SupportModuleItem;
